import React, { useRef, useEffect, useState } from "react";
import { Outlet, useLocation, NavLink, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useOcSessionStore } from "@/store/oc-session-store";
import { ocClearSessionToken, ocFetch } from "@/lib/oc-api";
import { OcSidebarNav, OC_NAV_ITEMS } from "./OcSidebarNav";
import { useOcTheme } from "@/hooks/useOcTheme";
import { ocHasPermission, type OcPermissionKey } from "@oc/types";

// Resolve the permission a given route path requires by matching it against the
// nav item whose href is the longest prefix of the path. Routes with no nav
// match (redirects, setup) are unguarded.
function ocRequiredPermissionForPath(pathname: string): OcPermissionKey | null {
  let best: { href: string; permission: OcPermissionKey } | null = null;
  for (const item of OC_NAV_ITEMS) {
    if (item.external || !item.permission) continue;
    if (pathname === item.href || pathname.startsWith(item.href + "/")) {
      if (!best || item.href.length > best.href.length) {
        best = { href: item.href, permission: item.permission };
      }
    }
  }
  return best?.permission ?? null;
}

function OcRouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const permissions = useOcSessionStore((s) => s.permissions);
  const required = ocRequiredPermissionForPath(location.pathname);
  if (required && !ocHasPermission(permissions, required)) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 dark:border-oc-navy-700 bg-white dark:bg-oc-navy-800 py-20 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.8} stroke="currentColor" className="h-6 w-6 text-amber-500 dark:text-amber-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
          </svg>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">You don't have access to this page</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">
            Your role doesn't include permission for this area. Contact an administrator if you need access.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

function OcShieldMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 54" fill="none" className={className} aria-hidden="true">
      <path d="M24 2 L44 10 L44 26 C44 37 34 45 24 50 C14 45 4 37 4 26 L4 10 Z"
            fill="none" stroke="#0ea5e9" strokeWidth="2.5"/>
      <path d="M13 26 L20 33 L35 18" stroke="#0ea5e9" strokeWidth="3.5"
            strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function OcThemeToggle() {
  const { isDark, toggleTheme } = useOcTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700 transition-colors"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
        </svg>
      )}
    </button>
  );
}

// ─── Breadcrumb helper ────────────────────────────────────────────────────────

const OC_ROUTE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  students: "Students",
  "provisioning-rules": "Provisioning Rules",
  "scheduled-deletions": "Scheduled Deletions",
  "slate-integration": "Slate Integration",
  "ad-connection": "AD Connection",
  agents: "Agents",
  "audit-log": "Audit Log",
  "password-reset": "Password Reset",
  reports: "Reports",
  analytics: "Analytics",
  settings: "Settings",
  setup: "Setup",
  general: "General",
  "users-roles": "Users & Roles",
  billing: "Billing",
  "disabled-users": "Disabled Users",
  "new-ad-users": "New AD Users",
  "new-azure-users": "New Azure Users",
  "slate-accepts": "Slate Accepts",
  saved: "Saved Reports",
  "email-templates": "Email Templates",
  workflows: "Workflows",
  connections: "Connections",
  credentials: "Credentials",
  developer: "Developer",
  usage: "Plan & Usage",
  support: "Support",
  sso: "SSO Settings",
};

const OC_BREADCRUMB_CHEVRON = (
  <svg
    className="h-3 w-3 text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

function OcBreadcrumb() {
  const location = useLocation();

  // Match nav item by exact path or prefix (for sub-routes)
  const navMatch = OC_NAV_ITEMS.find(
    (item) =>
      location.pathname === item.href ||
      location.pathname.startsWith(item.href + "/")
  );
  if (navMatch) {
    return (
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
        <span>{navMatch.group}</span>
        {OC_BREADCRUMB_CHEVRON}
        <span className="text-slate-600 dark:text-slate-300 font-medium">{navMatch.label}</span>
      </nav>
    );
  }

  const segments = location.pathname.split("/").filter(Boolean);

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        const label = OC_ROUTE_LABELS[seg] ?? seg;
        const href = "/" + segments.slice(0, idx + 1).join("/");

        return (
          <React.Fragment key={href}>
            {idx > 0 && OC_BREADCRUMB_CHEVRON}
            {isLast ? (
              <span className="text-slate-600 dark:text-slate-300 font-medium">{label}</span>
            ) : (
              <NavLink to={href} className="hover:text-slate-300 transition-colors">
                {label}
              </NavLink>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

interface OcPortalHeaderProps {
  onMenuOpen?: () => void;
  onLogout: () => void;
  sidebarCollapsed: boolean;
}

function OcPortalHeader({ onMenuOpen, onLogout, sidebarCollapsed }: OcPortalHeaderProps) {
  const tenant = useOcSessionStore((s) => s.tenant);
  const userName = useOcSessionStore((s) => s.userName);
  const userEmail = useOcSessionStore((s) => s.userEmail);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const displayName = userName ?? userEmail ?? "User";
  const companyName = tenant?.name ?? "";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <header
      className={clsx(
        "fixed top-0 right-0 left-0 z-20 flex items-center justify-between gap-4 px-4 border-b border-slate-200 dark:border-oc-navy-700 bg-white/90 dark:bg-oc-navy-900/90 backdrop-blur-sm oc-sidebar-transition",
        // Sidebar offset applies only at lg+, where the sidebar is fixed/visible.
        // On mobile the sidebar overlays, so the base px-4 keeps the hamburger reachable.
        sidebarCollapsed
          ? "lg:pl-[calc(1rem+var(--oc-sidebar-collapsed-width))]"
          : "lg:pl-[calc(1rem+var(--oc-sidebar-width))]"
      )}
      style={{ height: "var(--oc-header-height)" }}
    >
      {/* Left: mobile hamburger + logo (mobile) / breadcrumb (desktop) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onMenuOpen}
          className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700 transition-colors"
          aria-label="Open navigation"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        {/* Brand mark — mobile only */}
        <div className="lg:hidden flex items-center gap-2">
          <OcShieldMark className="h-7 w-7 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">OnboardConnect</span>
        </div>
        {/* Breadcrumb — desktop only */}
        <div className="hidden lg:block">
          <OcBreadcrumb />
        </div>
      </div>

      {/* Right: theme toggle + user dropdown */}
      <div className="flex items-center gap-2">
        <OcThemeToggle />

        {/* User chip with dropdown */}
        <div className="relative pl-2 border-l border-slate-200 dark:border-oc-navy-700" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2.5 rounded-lg px-2 py-1 hover:bg-slate-100 dark:hover:bg-oc-navy-700 transition-colors"
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <div
              className="h-7 w-7 rounded-full bg-oc-teal-500/20 flex items-center justify-center text-xs font-semibold text-oc-teal-400 flex-shrink-0"
              aria-hidden="true"
            >
              {initial}
            </div>
            <div className="hidden md:flex flex-col items-start min-w-0">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 leading-tight max-w-[140px] truncate">
                {displayName}
              </span>
              {companyName && (
                <span className="text-[10px] text-slate-400 dark:text-slate-500 leading-tight max-w-[140px] truncate">
                  {companyName}
                </span>
              )}
            </div>
            <svg
              className={clsx("hidden md:block h-3.5 w-3.5 text-slate-400 flex-shrink-0 transition-transform", menuOpen && "rotate-180")}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
            </svg>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-44 rounded-lg border border-slate-200 dark:border-oc-navy-600 bg-white dark:bg-oc-navy-800 shadow-lg py-1 z-50">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onLogout(); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-red-500/10 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-3.5 w-3.5 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

interface OcSidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

function OcSidebar({ mobileOpen = false, onMobileClose }: OcSidebarProps) {
  const { sidebarCollapsed, toggleSidebar } = useOcSessionStore();

  return (
    <aside
      className={clsx(
        "fixed left-0 top-0 bottom-0 z-30 flex flex-col",
        "border-r border-slate-200 dark:border-oc-navy-700 bg-white dark:bg-oc-navy-900",
        "oc-sidebar-transition",
        // Mobile: translate in/out; Desktop: always visible
        "lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
      )}
      style={{
        width: sidebarCollapsed
          ? "var(--oc-sidebar-collapsed-width)"
          : "var(--oc-sidebar-width)",
      }}
      aria-label="Sidebar"
    >
      {/* Sidebar top: aligns with header, shows brand */}
      <div
        className="flex items-center gap-2.5 px-3 border-b border-slate-200 dark:border-oc-navy-700 flex-shrink-0"
        style={{ height: "var(--oc-header-height)" }}
      >
        <OcShieldMark className="flex-shrink-0 h-7 w-7" />
        {!sidebarCollapsed && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">
              OnboardConnect
            </span>
            <span className="text-[10px] font-medium text-oc-teal-500 leading-tight">
              For Slate CRM
            </span>
          </div>
        )}
        {onMobileClose && (
          <button
            type="button"
            onClick={onMobileClose}
            className="lg:hidden ml-auto p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            aria-label="Close navigation"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Nav items */}
      <OcSidebarNav collapsed={sidebarCollapsed} />

      {/* Collapse toggle */}
      <div className="flex flex-col gap-1 px-2 py-3 border-t border-slate-200 dark:border-oc-navy-700 flex-shrink-0">
        <button
          type="button"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-oc-navy-700 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={clsx("h-4 w-4 flex-shrink-0 transition-transform", sidebarCollapsed && "rotate-180")}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5"
            />
          </svg>
          {!sidebarCollapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}

// ─── Banners ──────────────────────────────────────────────────────────────────

function OcTrialBanner({ daysRemaining }: { daysRemaining: number }) {
  return (
    <div
      className="sticky top-0 z-40 w-full bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center justify-center gap-2"
      role="status"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-3.5 w-3.5 flex-shrink-0"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
      <span>
        Your trial ends in{" "}
        <strong className="font-semibold">
          {daysRemaining} day{daysRemaining !== 1 ? "s" : ""}
        </strong>
        .{" "}
        <a href="/settings/billing" className="underline underline-offset-2 hover:text-amber-100">
          Upgrade now
        </a>
      </span>
    </div>
  );
}

function OcUsageWarningBanner({ percentUsed }: { percentUsed: number }) {
  return (
    <div
      className="sticky top-0 z-40 w-full bg-red-500/10 border-b border-red-500/20 px-4 py-2 text-xs text-red-700 dark:text-red-300 flex items-center justify-center gap-2"
      role="alert"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-3.5 w-3.5 flex-shrink-0"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
        />
      </svg>
      <span>
        You've used <strong className="font-semibold">{percentUsed}%</strong> of your annual provisioning limit.{" "}
        <a href="/plan-usage" className="underline underline-offset-2 hover:opacity-80">
          View plan details
        </a>{" "}
        or{" "}
        <a href="mailto:solutions@zentrosoft.com" className="underline underline-offset-2 hover:opacity-80">
          contact us to upgrade
        </a>
        .
      </span>
    </div>
  );
}

function OcImpersonationBanner({ impersonatedBy }: { impersonatedBy: string }) {
  return (
    <div
      className="sticky top-0 z-40 w-full bg-red-600/15 border-b border-red-500/30 px-4 py-2 text-xs text-red-700 dark:text-red-300 flex items-center justify-center gap-2"
      role="alert"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-3.5 w-3.5 flex-shrink-0"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>
      <span>
        Impersonation session active — logged in as{" "}
        <strong className="font-semibold">{impersonatedBy}</strong>. Actions are
        recorded in the audit log.
      </span>
    </div>
  );
}

// ─── Layout shell ─────────────────────────────────────────────────────────────

export function OcPortalLayout() {
  const { sidebarCollapsed, isTrial, trialDaysRemaining, isImpersonating, impersonatedBy, clearSession } =
    useOcSessionStore();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [usageWarningPct, setUsageWarningPct] = useState<number | null>(null);

  useEffect(() => {
    ocFetch<{ percentUsed: number | null; isUnlimited: boolean }>("/tenant/usage")
      .then((data) => {
        if (!data.isUnlimited && data.percentUsed !== null && data.percentUsed >= 90) {
          setUsageWarningPct(data.percentUsed);
        }
      })
      .catch(() => {});
  }, []);

  function handleLogout() {
    ocClearSessionToken();
    clearSession();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-oc-navy-900 text-slate-800 dark:text-slate-200">
      <OcSidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />

      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main content area — no margin on mobile (sidebar overlays); desktop offset by sidebar width */}
      <div
        className={clsx(
          "flex flex-col min-h-screen oc-sidebar-transition",
          sidebarCollapsed
            ? "lg:[margin-left:var(--oc-sidebar-collapsed-width)]"
            : "lg:[margin-left:var(--oc-sidebar-width)]"
        )}
      >
        <OcPortalHeader onMenuOpen={() => setMobileNavOpen(true)} onLogout={handleLogout} sidebarCollapsed={sidebarCollapsed} />

        {/* Page content — offset below fixed header */}
        <main
          className="flex-1 flex flex-col"
          style={{ paddingTop: "var(--oc-header-height)" }}
        >
          {/* Sticky banners (stack below header) */}
          {isImpersonating && impersonatedBy && (
            <OcImpersonationBanner impersonatedBy={impersonatedBy} />
          )}
          {isTrial && trialDaysRemaining !== null && (
            <OcTrialBanner daysRemaining={trialDaysRemaining} />
          )}
          {usageWarningPct !== null && (
            <OcUsageWarningBanner percentUsed={usageWarningPct} />
          )}

          {/* Route outlet */}
          <div className="flex-1 p-6">
            <OcRouteGuard>
              <Outlet />
            </OcRouteGuard>
          </div>
        </main>
      </div>
    </div>
  );
}
